import { faBolt, faBoltSlash, faGridRound2Plus, faSave } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  DropDownButton,
  DropDownButtonItem,
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
} from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { Splitter } from "@progress/kendo-react-layout";
import { ApprovalDiagramModel } from "../../Automation/DiagramModels/ApprovalDiagramModel";
import { CustomDiagramModel } from "../../Automation/DiagramModels/CustomDiagramModel";
import { NotificationDiagramModel } from "../../Automation/DiagramModels/NotificationDiagramModel";
import { AdaptiveNode } from "../../Automation/Nodes/NodeFactory";
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { If } from "../../Common/UI/IfComponent";
import {
  automationSessionData,
  automationSplitterPaneSize,
  automationSplitterPanes,
  selectedAutomationTreeViewItem,
  showAutomationsWindow,
  templateData,
} from "../NewTemplateSignals";
import { AtlasDiagramWidget } from "./AtlasDiagramWidget";
import { AutomationPropertiesPane } from "./AutomationPropertiesPane";
import { AutomationTreeView } from "./AutomationTreeView";

export const AutomationsWindow = () => {
  const addNode = ({ type }) => {
    if (!selectedAutomationTreeViewItem.value) return;
    const newModel = automationSessionData.value.find(
      (automation) => automation.id === selectedAutomationTreeViewItem.value
    ).model;

    const NodeConstructor = AdaptiveNode(type);
    const newNode = new NodeConstructor({});

    newNode.registerListener({
      selectionChanged: (event) => {
        newNode.selected = event.isSelected;
        automationSessionData.value = automationSessionData.value.map((automation) => {
          if (automation.id === selectedAutomationTreeViewItem.value) {
            return {
              ...automation,
              model: newModel,
            };
          }
          return automation;
        });
      },
    });
    newModel.addNode(newNode);

    automationSessionData.value = automationSessionData.value.map((automation) => {
      if (automation.id === selectedAutomationTreeViewItem.value) {
        return {
          ...automation,
          model: newModel,
        };
      }
      return automation;
    });
  };

  const saveChanges = () => {
    const newTemplateData = JSON.tryParse(JSON.stringify(templateData.value));

    newTemplateData.properties.Automations = automationSessionData.value.map((automation) => {
      return {
        ...automation,
        model: automation.model.serialize(),
      };
    });
    templateData.value = newTemplateData;
    showAutomationsWindow.value = false;
    console.log("Saved changes to template data");
  };

  return (
    <If condition={showAutomationsWindow.value}>
      <Window
        title="Automations"
        initialHeight={900}
        initialWidth={1600}
        onClose={() => {
          showAutomationsWindow.value = false;
          selectedAutomationTreeViewItem.value = null;
          automationSessionData.value = [];
        }}
      >
        <div className="!k-h-full !k-w-full k-d-flex k-flex-col">
          <Toolbar size={null}>
            <ColorChangingIcon
              title="Save Template"
              name="btnTemplateSubmit"
              icon={faSave}
              initialPrimaryColor="#000"
              initialSecondaryColor="#5fd255"
              primaryOpacity={0.75}
              secondaryOpacity={1}
              initialSize={32}
              hoverSize={32}
              margin="0 10px 0 10px"
              onClick={saveChanges}
            />
            <ToolbarSeparator />
            <ToolbarItem>
              <DropDownButton
                text="Add Automation"
                className="!k-fs-xl !k-rounded-0"
                fillMode="flat"
                startIcon={
                  <FontAwesomeIcon
                    icon={faBolt}
                    className="k-mr-1"
                  />
                }
                onItemClick={(e) => e.item.onClick()}
              >
                <DropDownButtonItem
                  text="Notification Automation"
                  onClick={() => {
                    const newSessionData = [
                      ...automationSessionData.value,
                      NotificationDiagramModel(),
                    ];
                    automationSessionData.value = newSessionData;
                  }}
                />
                <DropDownButtonItem
                  text="Approval Automation"
                  onClick={() => {
                    const newSessionData = [...automationSessionData.value, ApprovalDiagramModel()];
                    automationSessionData.value = newSessionData;
                  }}
                />
                <DropDownButtonItem
                  text="Custom Automation"
                  onClick={() => {
                    const newSessionData = [...automationSessionData.value, CustomDiagramModel()];
                    automationSessionData.value = newSessionData;
                  }}
                />
              </DropDownButton>
            </ToolbarItem>
            <Button
              className="!k-fs-xl !k-rounded-0"
              fillMode={"flat"}
              onClick={() => {
                const newSessionData = [...automationSessionData.value];
                const automationIndex = newSessionData.findIndex(
                  (automation) => automation.id === selectedAutomationTreeViewItem.value
                );
                newSessionData.splice(automationIndex, 1);
                automationSessionData.value = newSessionData;
                selectedAutomationTreeViewItem.value = null;
              }}
            >
              <FontAwesomeIcon
                icon={faBoltSlash}
                className="k-mr-1"
              />
              Delete Automation
            </Button>
            <ToolbarSeparator />
            <ToolbarItem>
              <DropDownButton
                text="Add Node"
                className="!k-fs-xl !k-rounded-0"
                fillMode="flat"
                disabled={!selectedAutomationTreeViewItem.value}
                startIcon={
                  <FontAwesomeIcon
                    icon={faGridRound2Plus}
                    className="k-mr-1"
                  />
                }
                onClick={(e) => console.log(e)}
                onItemClick={(e) => e.item.onClick()}
              >
                <DropDownButtonItem
                  text="Variable"
                  onClick={() => addNode({ type: "VariableNode" })}
                />
                <DropDownButtonItem
                  text="Function"
                  onClick={() => addNode({ type: "FunctionNode" })}
                />
                <DropDownButtonItem
                  text="Event"
                  onClick={() => addNode({ type: "EventNode" })}
                />
                <DropDownButtonItem
                  text="Condition"
                  onClick={() => addNode({ type: "ConditionNode" })}
                />
              </DropDownButton>
            </ToolbarItem>
          </Toolbar>
          <Splitter
            className="!k-overflow-y-auto k-flex-1"
            orientation="horizontal"
            panes={automationSplitterPanes.value}
            onChange={(e) => {
              automationSplitterPaneSize.value = e.newState;
            }}
          >
            <AutomationTreeView />
            <AtlasDiagramWidget />
            <AutomationPropertiesPane />
          </Splitter>
        </div>
      </Window>
    </If>
  );
};
